"use client";

import React from "react";
import Image from "next/image";
import { partnerLogos } from "../../data.js";
import { useTranslation } from "react-i18next";
import { motion, Variants } from "framer-motion";
import { Award, Building2, Globe, Users, MapPin } from "lucide-react";

export default function Partners() {
  const { t } = useTranslation();

  const logoRows = [
    partnerLogos.filter((_, index) => index % 2 === 0),
    partnerLogos.filter((_, index) => index % 2 !== 0),
  ];

  // List Sekolah Garuda 2026 berdasarkan gambar referensi image_f5953f.png
  const sekolahGaruda = [
    "Cahaya Rancamaya Islamic Boarding School",
    "SMA Fatih Bilingual Boarding School",
    "SMP-SMA Kesatuan Bangsa",
    "SMA Kharisma Bangsa",
    "SMA Pribadi Bandung",
    "SMA Semesta Bilingual Boarding School",
    "SMA Dwiwarna (Boarding School)",
    "SMA TNA Fatih"
  ];

  // Variasi Animasi Framer Motion untuk Efek Fade-In saat Scroll
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 15 },
    },
  };

  return (
    <section id="partners" className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">

      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] right-[5%] w-72 h-72 bg-[#12AAC9]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[20%] left-[5%] w-96 h-96 bg-[#7897D6]/5 rounded-full blur-3xl" />
      </div>

      <div className="w-[92%] max-w-[1800px] mx-auto px-5 lg:px-8 relative z-10">

        {/* =========================================================================
            PART 1: PARTNERS CAROUSEL (Seksi Atas)
            ========================================================================= */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={containerVariants}
          className="mb-20"
        >
          {/* Header Section */}
          <motion.div
            variants={itemVariants}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            {/* Badge */}
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#12AAC9]/10 to-[#7897D6]/10 border border-[#12AAC9]/20 mb-6"
            >
              <Building2 size={14} className="text-[#12AAC9]" />
              <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-[#12AAC9]">
                {t("Partners.tag")}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h2
              variants={itemVariants}
              className="text-h2 md:text-[2.2rem] text-slate-900 leading-tight tracking-tight mb-6"
            >
              {t("Partners.title")}
            </motion.h2>

            {/* Description */}
            <motion.p
              variants={itemVariants}
              className="text-base md:text-lg leading-relaxed text-slate-600 mb-8"
            >
              {t("Partners.desc")}
            </motion.p>

            {/* Stats Row */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap items-center justify-center gap-8 mt-10 pt-10 border-t border-slate-200/60"
            >
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#12AAC9] to-[#7897D6] flex items-center justify-center">
                  <Building2 size={18} className="text-white" />
                </div>
                <div className="text-left">
                  <p className="text-lg font-bold text-slate-900">175+</p>
                  <p className="text-xs text-slate-500">Sekolah</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#12AAC9] to-[#7897D6] flex items-center justify-center">
                  <Globe size={18} className="text-white" />
                </div>
                <div className="text-left">
                  <p className="text-lg font-bold text-slate-900">5+</p>
                  <p className="text-xs text-slate-500">Negara</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#12AAC9] to-[#7897D6] flex items-center justify-center">
                  <Users size={18} className="text-white" />
                </div>
                <div className="text-left">
                  <p className="text-lg font-bold text-slate-900">42K+</p>
                  <p className="text-xs text-slate-500">Pengguna</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Marquee Carousel */}
          <motion.div
            variants={itemVariants}
            className="relative left-1/2 w-screen -translate-x-1/2 overflow-hidden md:left-auto md:w-full md:translate-x-0"
          >
            {/* Gradient Fade Edges */}
            <div className="hidden md:block absolute left-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-r from-slate-50 to-transparent z-20 pointer-events-none" />
            <div className="hidden md:block absolute right-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-l from-slate-50 to-transparent z-20 pointer-events-none" />

            <div className="overflow-hidden py-3">
              {logoRows.map((logos, rowIndex) => (
                <div key={rowIndex} className="overflow-hidden py-3">
                  <motion.div
                    className="flex w-max gap-5"
                    animate={{ x: rowIndex === 0 ? ["0%", "-50%"] : ["-50%", "0%"] }}
                    transition={{
                      ease: "linear",
                      duration: rowIndex === 0 ? 90 : 100,
                      repeat: Infinity,
                    }}
                  >
                    {[...logos, ...logos].map((partner, index) => (
                      <motion.div
                        key={`${partner.id}-${index}`}
                        className="group flex items-center justify-center w-[170px] h-[95px] md:w-[220px] md:h-[120px] lg:w-[250px] lg:h-[130px]
                          bg-white rounded-2xl
                          border border-slate-100
                          shadow-[0_4px_20px_rgba(0,0,0,0.04)]
                          hover:shadow-[0_15px_40px_rgba(18,170,201,0.12)]
                          hover:-translate-y-2
                          hover:border-[#12AAC9]/30
                          transition-all duration-300 ease-out
                          shrink-0 cursor-pointer
                          overflow-hidden"
                        whileHover={{ scale: 1.02 }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                        <div className="relative w-[120px] h-[55px] md:w-[150px] md:h-[70px] lg:w-[180px] lg:h-[80px]">
                          <Image
                            src={partner.image}
                            alt={partner.alt || "Partner Logo"}
                            fill
                            sizes="(max-width: 768px) 120px, (max-width: 1024px) 150px, 180px"
                            className="object-contain filter group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                          />
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        {/* =========================================================================
            PART 2: SEKOLAH GARUDA 2026 INFO BANNER
            ========================================================================= */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={containerVariants}
        >
          {/* Banner Utama - selaras dengan card "Penilaian Guru & Siswa" di Benefits */}
          <motion.div
            variants={itemVariants}
            className="relative rounded-[2rem] md:rounded-[2.5rem] p-8 md:p-14 lg:p-16 text-white overflow-hidden bg-gradient-to-br from-[#1bacee] from-40% to-[#08547e]"
          >
            

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center relative z-10">

              {/* Bagian Kiri Banner */}
              <div className="flex flex-col items-start">
                {/* Badge dengan Glassmorphism */}
                <motion.div
                  className="flex items-center gap-3 mb-6 bg-white/20 backdrop-blur-xl px-5 py-2.5 rounded-full border border-white/30 shadow-lg"
                  whileHover={{ scale: 1.05, y: -2 }}
                  transition={{ type: "spring", stiffness: 300, damping: 15 }}
                >
                  <Award size={18} className="text-yellow-300 drop-shadow-lg" />
                  <span className="text-[11px] font-bold uppercase tracking-wider">
                    {t("Partners.sekolah_garuda.badge")}
                  </span>
                </motion.div>

                {/* Title */}
                <motion.h3
                  variants={itemVariants}
                  className="text-[2rem] md:text-[2.5rem] lg:text-[3rem] font-black leading-tight mb-5 tracking-tight drop-shadow-lg"
                >
                  {t("Partners.sekolah_garuda.title")}
                </motion.h3>

                {/* Description */}
                <motion.p
                  variants={itemVariants}
                  className="text-white/90 text-[15px] md:text-base leading-7 mb-6 text-balance drop-shadow"
                >
                  {t("Partners.sekolah_garuda.desc")}
                </motion.p>

                {/* Ministry Indicator */}
                <motion.div
                  variants={itemVariants}
                  className="pl-4 border-l-2 border-white/40 text-white/80 text-[12px] md:text-[14px] italic leading-6 drop-shadow"
                >
                  {t("Partners.sekolah_garuda.desc")}
                </motion.div>
              </div>

              {/* Bagian Kanan Banner - Daftar Sekolah ala Sticky Notes */}
              <motion.div variants={itemVariants} className="relative">
                <h4 className="text-[13px] font-bold uppercase tracking-widest text-white/90 mb-5 border-b border-white/20 pb-3 drop-shadow">
                  {t("Partners.sekolah_garuda.subtitle")}
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
                  {sekolahGaruda.map((namaSekolah, idx) => {
                    // Alternating rotation for a hand-placed sticky-note feel
                    const rotations = [-3, 2, -2, 3, 2, -3, 3, -2];
                    const rotate = rotations[idx % rotations.length];
                    return (
                      <motion.div
                        key={idx}
                        className="relative bg-[#FFF7C2] text-slate-800 rounded-sm shadow-[0_6px_16px_rgba(0,0,0,0.25)] px-4 pt-5 pb-3.5 min-h-[76px] flex items-center"
                        style={{ rotate }}
                        whileHover={{ rotate: 0, scale: 1.04, y: -3 }}
                        transition={{ type: "spring", stiffness: 220, damping: 14 }}
                      >
                        {/* Pin icon at the top edge */}
                        <MapPin
                          size={18}
                          className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-red-500 fill-red-500 drop-shadow-md"
                        />
                        {/* Ruled-paper lines */}
                        <div
                          aria-hidden
                          className="pointer-events-none absolute inset-0 rounded-sm opacity-40 [background-image:repeating-linear-gradient(transparent,transparent_17px,rgba(30,58,138,0.35)_18px)]"
                        />
                        <span className="relative z-10 text-[12px] md:text-[13px] font-bold leading-snug">
                          {namaSekolah}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>

            </div>
          </motion.div>
        </motion.div>

      </div>
    </section>
  );
}
